// ============================================================
// PREZENCE — Departments management (admin/HR)
// Create departments, rename them, assign a head, delete empty ones.
// Employees are assigned to these via dropdown (no more typos).
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import './departments.css';

export default function Departments() {
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  // Per-card pending head selection (so choosing in the dropdown does NOT assign
  // until the user clicks "Assign"). Keyed by department id.
  const [pendingHead, setPendingHead] = useState({});
  const [savingHead, setSavingHead] = useState(null);

  // Modal state: { type: 'rename'|'delete', dept } or null.
  const [modal, setModal] = useState(null);
  const [modalValue, setModalValue] = useState('');
  const [modalBusy, setModalBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([
        api.get('/departments'),
        api.get('/users'),
      ]);
      setDepts(d.data.departments || []);
      setUsers((u.data.data || u.data.users || []).filter(x => x.is_active !== false));
      setPendingHead({}); // reset pending selections after a reload
    } catch (e) {
      toast.error('Failed to load departments');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post('/departments', { name: newName.trim() });
      setNewName('');
      toast.success('Department created');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not create department');
    } finally { setCreating(false); }
  };

  // The selected value for a card = pending choice if any, else the saved head.
  const selectedHead = (d) =>
    pendingHead[d.id] !== undefined ? pendingHead[d.id] : (d.head_user_id || '');

  // Has the user changed the dropdown away from what's saved?
  const headChanged = (d) =>
    pendingHead[d.id] !== undefined && pendingHead[d.id] !== (d.head_user_id || '');

  const assignHead = async (d) => {
    const headId = pendingHead[d.id] ?? (d.head_user_id || '');
    setSavingHead(d.id);
    try {
      await api.patch(`/departments/${d.id}`, { head_user_id: headId || null });
      toast.success(headId ? 'Head assigned' : 'Head cleared');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not assign head');
    } finally { setSavingHead(null); }
  };

  // ── Modal actions ──
  const openRename = (d) => { setModal({ type: 'rename', dept: d }); setModalValue(d.name); };
  const openDelete = (d) => {
    if (d.member_count > 0) { toast.error('Move employees out of this department first'); return; }
    setModal({ type: 'delete', dept: d }); setModalValue('');
  };
  const closeModal = () => { if (!modalBusy) { setModal(null); setModalValue(''); } };

  const confirmModal = async () => {
    if (!modal) return;
    setModalBusy(true);
    try {
      if (modal.type === 'rename') {
        const name = modalValue.trim();
        if (!name) { toast.error('Name cannot be empty'); setModalBusy(false); return; }
        if (name === modal.dept.name) { closeModalForce(); return; }
        await api.patch(`/departments/${modal.dept.id}`, { name });
        toast.success('Department renamed');
      } else if (modal.type === 'delete') {
        await api.delete(`/departments/${modal.dept.id}`);
        toast.success('Department deleted');
      }
      closeModalForce();
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Action failed');
    } finally { setModalBusy(false); }
  };
  const closeModalForce = () => { setModal(null); setModalValue(''); setModalBusy(false); };

  return (
    <div className="dp-root">
      <div className="dp-header">
        <div>
          <h1 className="dp-title">Departments</h1>
          <p className="dp-sub">Organise your team. Assign a head to each department.</p>
        </div>
      </div>

      <div className="dp-create">
        <input
          className="dp-input"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && create()}
          placeholder="New department name (e.g. Business Development)"
        />
        <button className="dp-add" onClick={create} disabled={creating}>
          {creating ? 'Adding…' : '+ Add Department'}
        </button>
      </div>

      {loading ? (
        <div className="dp-loading"><div className="spinner" /></div>
      ) : depts.length === 0 ? (
        <div className="dp-empty">No departments yet. Create your first one above.</div>
      ) : (
        <div className="dp-list">
          {depts.map(d => (
            <div className="dp-card" key={d.id}>
              <div className="dp-card-main">
                <div className="dp-name">{d.name}</div>
                <div className="dp-meta">{d.member_count} member{d.member_count === 1 ? '' : 's'}</div>
              </div>

              <div className="dp-head">
                <label className="dp-head-label">
                  HEAD {d.head_vacant && <span className="dp-vacant">· vacant, reassign</span>}
                </label>
                <div className="dp-head-row">
                  <select
                    className={`dp-select ${d.head_vacant ? 'dp-select-vacant' : ''}`}
                    value={selectedHead(d)}
                    onChange={e => setPendingHead(p => ({ ...p, [d.id]: e.target.value }))}
                  >
                    <option value="">— None —</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                  <button
                    className="dp-assign"
                    disabled={!headChanged(d) || savingHead === d.id}
                    onClick={() => assignHead(d)}
                  >
                    {savingHead === d.id ? 'Saving…' : 'Assign'}
                  </button>
                </div>
              </div>

              <div className="dp-actions">
                <button className="dp-btn" onClick={() => openRename(d)}>Rename</button>
                <button className="dp-btn dp-danger" onClick={() => openDelete(d)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Clean in-app modal (replaces window.prompt/confirm) ── */}
      {modal && (
        <div className="dp-modal-overlay" onClick={closeModal}>
          <div className="dp-modal" onClick={e => e.stopPropagation()}>
            {modal.type === 'rename' ? (
              <>
                <h3 className="dp-modal-title">Rename Department</h3>
                <p className="dp-modal-sub">Give “{modal.dept.name}” a new name.</p>
                <input
                  className="dp-modal-input"
                  autoFocus
                  value={modalValue}
                  onChange={e => setModalValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && confirmModal()}
                  placeholder="Department name"
                />
              </>
            ) : (
              <>
                <h3 className="dp-modal-title">Delete Department</h3>
                <p className="dp-modal-sub">
                  Delete “{modal.dept.name}”? This can’t be undone.
                  {modal.dept.member_count > 0 && ' Move its employees out first.'}
                </p>
              </>
            )}
            <div className="dp-modal-actions">
              <button className="dp-modal-cancel" onClick={closeModal} disabled={modalBusy}>Cancel</button>
              <button
                className={`dp-modal-confirm ${modal.type === 'delete' ? 'dp-modal-danger' : ''}`}
                onClick={confirmModal}
                disabled={modalBusy}
              >
                {modalBusy ? 'Working…' : (modal.type === 'rename' ? 'Save' : 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
